import { CdkMenuTrigger } from '@angular/cdk/menu';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import { FieldType } from '../../enums/field-type.enum';
import { CoreApiService } from '../../../core-api.service';
import { GroupedOption, Option } from '../../models/option.model';
import { Bucket } from '../../models/search/search-facets.model';
import { SearchFieldAggregation } from '../../models/search/search-field-aggregation.model';
import { SearchField } from '../../models/search/search-field.model';
import { SearchReturnType } from '../../models/search/search-query.model';
import { ProfileService } from '../../../profile/profile.service';
import { groupProjects } from '../../projects.utils';
import { ProjectsService } from '../../../projects/projects.service';
import { sortGroupedOptions } from '../../select-with-groups.utils';
import { replayValue } from '../../../shared/observable.utils';
import { SearchMode, SidebarService } from '../../../sidebar.service';
import { isSearchPath } from '../../../search/search.utils';

export enum AppSideBarSearchEnum {
  Classification = 'classification',
  CustomField = 'customField',
  CustomFieldsList = 'customFieldsList',
  FileType = 'fileType',
  From = 'from',
  Modality = 'modality',
  Project = 'project',
  SearchTerm = 'searchTerm',
  Sex = 'sex',
  SubjectAgeFrom = 'subjectAgeFrom',
  SubjectAgeTo = 'subjectAgeTo',
  To = 'to',
}

export enum FiltersRangeEnum {
  Max = 'lte',
  Min = 'gte',
}

export enum CustomControllerFields {
  Max = 'lte',
  Min = 'gte',
  Name = 'name',
  Type = 'type',
  Value = 'value',
}

const fileTypePath = 'file.type';
const fileModalityPath = 'file.modality';
const classificationListPath = 'file.classification_list';
const subjectSexPath = 'subject.sex';
const projectIdPath = 'project._id';
const rangePath = 'session.timestamp';
const subjectAgesPath = 'session.age';
const customFieldBooleanFalse = 'false';
const customFieldBooleanTrue = 'true';
const numberOfSecondsInOneYear = 31556952;

@Component({
  selector: 'app-side-bar-search',
  templateUrl: './app-side-bar-search.component.html',
  styleUrls: ['./app-side-bar-search.component.scss'],
  host: {
    'test-id': 'app-side-bar-search',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppSideBarSearchComponent implements OnInit, OnDestroy {
  @ViewChildren(CdkMenuTrigger) triggers: QueryList<CdkMenuTrigger>;

  form = this.formBuilder.group({
    [AppSideBarSearchEnum.FileType]: new FormArray([]),
    [AppSideBarSearchEnum.Modality]: new FormArray([]),
    [AppSideBarSearchEnum.Classification]: new FormArray([]),
    [AppSideBarSearchEnum.SearchTerm]: new FormControl(''),
    [AppSideBarSearchEnum.Sex]: new FormArray([]),
    [AppSideBarSearchEnum.SubjectAgeFrom]: new FormControl(''),
    [AppSideBarSearchEnum.SubjectAgeTo]: new FormControl(''),
    [AppSideBarSearchEnum.CustomField]: new FormControl(''),
    [AppSideBarSearchEnum.Project]: new FormControl(''),
    [AppSideBarSearchEnum.From]: new FormControl(''),
    [AppSideBarSearchEnum.To]: new FormControl(''),
    [AppSideBarSearchEnum.CustomFieldsList]: new FormArray([]),
  });
  lastNonSearchUrl = '';

  private readonly subscriptions = { form: Subscription.EMPTY, fields: Subscription.EMPTY, customTextInputs: [], externalProjectChange: Subscription.EMPTY };

  private observer: MutationObserver;

  protected readonly customFieldBooleanFalse = customFieldBooleanFalse;
  protected readonly customFieldBooleanTrue = customFieldBooleanTrue;
  protected readonly customFiltersWithRange = [FieldType.Float, FieldType.Integer, FieldType.Date];
  protected readonly controlsEnum = AppSideBarSearchEnum;
  protected readonly customControllerFields = CustomControllerFields;
  protected readonly fieldType = FieldType;
  protected readonly projectsGroups$: Observable<GroupedOption[]> = this.projectsService.getProjects().pipe(map((projects) => {
    this.projectsCount = projects.length;
    return sortGroupedOptions(groupProjects(projects));
  }));

  readonly facets$ = combineLatest([
    replayValue(this.form.get(AppSideBarSearchEnum.SearchTerm)).pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(search => {
        this.sidebarService.search.query = search;
        this.sidebarService.performSearch();
      }),
    ),
    this.profileService.exhaustiveMode$,
  ]).pipe(switchMap(([searchString, mode]) => {
    return this.coreApi.dataexplorer.facets({ return_type: SearchReturnType.Session, all_data: mode, search_string: searchString })
      .pipe(map((data) => ({
          fileType: data.facets.by_file[fileTypePath].buckets,
          modality: data.facets.by_file[fileModalityPath].buckets,
          classification: data.facets.by_file[classificationListPath].buckets,
          sex: data.facets.by_session[subjectSexPath].buckets,
          timestamp: data.facets.by_session[rangePath],
        })), tap((data) => {
          this.setBuckets(this.createFormGroups(data.fileType), AppSideBarSearchEnum.FileType);
          this.setBuckets(this.createFormGroups(data.modality), AppSideBarSearchEnum.Modality);
          this.setBuckets(this.createFormGroups(data.classification), AppSideBarSearchEnum.Classification);
          this.setBuckets(this.createFormGroups(data.sex), AppSideBarSearchEnum.Sex);
          // internal testing lead to the conclusion we do not want to preset this for users, leaving for posterity
          // this.setInitialAgeRange(data.timestamp?.['min_as_string'], data.timestamp?.['max_as_string'])
        }));
  }));

  protected isAdvancedSearchMode = false;
  protected customFieldOptions$ = new Subject<SearchField[]>();
  protected typeahead = {};
  protected projectsCount = 0;
  protected selectedProjectFilter: Option; // stored so we can access the label in the template

  protected customMenuPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -6,
    },
  ]

  get fileTypeControls(): AbstractControl[] {
    return (this.form.get(AppSideBarSearchEnum.FileType) as FormArray).controls;
  }

  get modalityControls(): AbstractControl[] {
    return (this.form.get(AppSideBarSearchEnum.Modality) as FormArray).controls;
  }

  get classificationControls(): AbstractControl[] {
    return (this.form.get(AppSideBarSearchEnum.Classification) as FormArray).controls;
  }

  get sexControls(): AbstractControl[] {
    return (this.form.get(AppSideBarSearchEnum.Sex) as FormArray).controls;
  }

  get customFields(): FormArray {
    return this.form.get(AppSideBarSearchEnum.CustomFieldsList) as FormArray;
  }

  get isAdvancedQuery(): boolean {
    return window.location.hash === '#/search/advanced';
  }

  constructor(
    private changeDetector: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private coreApi: CoreApiService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    private sidebarService: SidebarService,
  ) {}

  ngOnInit(): void {
    if (document) {
      this.observer = new MutationObserver(() => setTimeout(() => this.checkUrl()));
      this.observer.observe(document, { subtree: true, childList: true });
    }

    this.sidebarService.stateChange$.subscribe((stateChange) => {
      if (isSearchPath(stateChange?.next?.name) && !isSearchPath(stateChange?.prev?.name)) {
        this.lastNonSearchUrl = stateChange?.prev?.name;
      }
    });

    this.subscriptions.externalProjectChange = this.sidebarService.state$.pipe(
      distinctUntilChanged(),
      filter(state => state === 'search'),
      switchMap(() => this.sidebarService.projectContext$),
      switchMap((projectId) => {
        return projectId ? this.projectsService.getProject(projectId) : of(null);
      }),
      distinctUntilChanged((prev, curr) => prev?._id === curr?._id),
    ).subscribe(project => {
      if (project) {
        this.selectProjectFilter({ value: project._id, label: project.label });
      }
    });

    this.subscriptions.form = combineLatest([
      this.form.valueChanges.pipe(debounceTime(200)),
      this.profileService.exhaustiveMode$,
    ]).pipe(
      distinctUntilChanged(([prev, prevExhaustive], [curr, currExhaustive]) => {
        if (prevExhaustive !== currExhaustive) {
          this.sidebarService.search.allData = currExhaustive;
          return false;
        }
        for (const key in prev) {
          if (key === AppSideBarSearchEnum.CustomField) {
            continue;
          }

          if (Array.isArray(curr[key])) {
            if (key === AppSideBarSearchEnum.CustomFieldsList) {
              if (prev[key]?.length > curr[key].length) {
                return false;
              }

              for (let i = 0; i < curr[key].length; i++) {
                const currentCustomField = curr[key][i];
                const prevCustomField = prev[key][i];

                if (this.customFiltersWithRange.includes(currentCustomField[CustomControllerFields.Type])) {
                  if (!currentCustomField[CustomControllerFields.Min] && !currentCustomField[CustomControllerFields.Max]) {
                    continue;
                  }

                  if (currentCustomField[CustomControllerFields.Min] !== prevCustomField?.[CustomControllerFields.Min] || currentCustomField[CustomControllerFields.Max] !== prevCustomField?.[CustomControllerFields.Max]) {
                    return false;
                  }
                }
                else {
                  if (!currentCustomField[CustomControllerFields.Value] && !prevCustomField?.[CustomControllerFields.Value]) {
                    continue;
                  }

                  if (currentCustomField[CustomControllerFields.Value] !== prevCustomField?.[CustomControllerFields.Value]) {
                    return false;
                  }
                }
              }
            } else {
              for (let i = 0; i < curr[key].length; i++) {
                if (curr[key][i]?.selected !== prev[key][i]?.selected) {
                  return false;
                }
              }
            }
          } else if (prev[key] !== curr[key]) {
            return false;
          }
        }

        return true;
      }),
      map(([val]) => val),
    ).subscribe((value) => {
      const filters = [];
      const selectedFileTypes = value[AppSideBarSearchEnum.FileType].filter(fileType => fileType.selected);
      const selectedModality = value[AppSideBarSearchEnum.Modality].filter(modality => modality.selected);
      const selectedClassification = value[AppSideBarSearchEnum.Classification].filter(classification => classification.selected);
      const selectedSex = value[AppSideBarSearchEnum.Sex].filter(sex => sex.selected);

      if (selectedFileTypes.length) {
        const filter = { terms: { [fileTypePath]: selectedFileTypes.map(fileType => fileType.key) } };
        filters.push(filter);
      }

      if (selectedModality.length) {
        const filter = { terms: { [fileModalityPath]: selectedModality.map(modality => modality.key) } };
        filters.push(filter);
      }

      if (selectedClassification.length) {
        const filter = { terms: { [classificationListPath]: selectedClassification.map(classification => classification.key) } };
        filters.push(filter);
      }

      if (selectedSex.length) {
        const filter = { terms: { [subjectSexPath]: selectedSex.map(sex => sex.key) } };
        filters.push(filter);
      }

      if (value[AppSideBarSearchEnum.SubjectAgeFrom] || value[AppSideBarSearchEnum.SubjectAgeTo]) {
        const range = {};

        if (value[AppSideBarSearchEnum.SubjectAgeFrom]) {
          range[FiltersRangeEnum.Min] = Number(value[AppSideBarSearchEnum.SubjectAgeFrom]) * numberOfSecondsInOneYear;
        }

        if (value[AppSideBarSearchEnum.SubjectAgeTo]) {
          range[FiltersRangeEnum.Max] = Number(value[AppSideBarSearchEnum.SubjectAgeTo]) * numberOfSecondsInOneYear;
        }

        filters.push({ range: { [subjectAgesPath]: range } });
      }

      if (value[AppSideBarSearchEnum.Project]) {
        const filter = { term: { [projectIdPath]: value[AppSideBarSearchEnum.Project] } };
        filters.push(filter);
      }

      if (value[AppSideBarSearchEnum.To] || value[AppSideBarSearchEnum.From]) {
        const range = {};

        if (value[AppSideBarSearchEnum.From]) {
          range[FiltersRangeEnum.Min] = this.parseFromDateToTimestamp(value[AppSideBarSearchEnum.From]);
        }

        if (value[AppSideBarSearchEnum.To]) {
          range[FiltersRangeEnum.Max] = this.parseToDateToTimestamp(value[AppSideBarSearchEnum.To]);
        }

        filters.push({ range: { [rangePath]: range } });
      }

      this.sidebarService.search.sidebarFilters = [...filters, ...this.getCustomFilters()];
      this.sidebarService.performSearch();
    });

    this.subscriptions.fields = this.form.get(AppSideBarSearchEnum.CustomField).valueChanges.pipe(
      filter(Boolean),
      debounceTime(200),
      switchMap(field => this.coreApi.dataexplorer.fields({ field })),
      map(data => data.slice(0, 20)),
    ).subscribe(suggestions => {
      const fieldOptions = suggestions.map(suggestion => ({ value: suggestion.name, title: suggestion.name }));

      if (fieldOptions.length) {
        this.triggers.first.open();
      } else {
        this.triggers.first.close();
      }

      this.customFieldOptions$.next(suggestions);
    });
  }

  ngOnDestroy(): void {
    for (const key in this.subscriptions) {
      if (Array.isArray(this.subscriptions[key])) {
        this.subscriptions[key].forEach(subscription => subscription.unsubscribe());
      } else {
        this.subscriptions[key].unsubscribe();
      }
    }

    this.observer.disconnect();
  }

  getCustomFilters(): Record<string, string>[] {
    const filters = [];
    this.customFields.controls.forEach((selectedCustomField) => {
      const value = selectedCustomField.value;
      if (value[CustomControllerFields.Type] === FieldType.String && value[CustomControllerFields.Value]) {
        const filter = { match: {} };
        filter.match[value[CustomControllerFields.Name]] = value[CustomControllerFields.Value];
        filters.push(filter);
      }
      else if (value[CustomControllerFields.Type] === FieldType.Boolean) {
        const filter = { term: {} };
        filter.term[value[CustomControllerFields.Name]] = value[CustomControllerFields.Value] === this.customFieldBooleanTrue;

        filters.push(filter);
      }
      else if ((value[CustomControllerFields.Min] || value[CustomControllerFields.Max]) && value[CustomControllerFields.Type] === FieldType.Date) {
        const filter = { range: {} };
        const key = value[CustomControllerFields.Name];
        filter.range[key] = {};

        if (value[CustomControllerFields.Min]) {
          filter.range[key][FiltersRangeEnum.Min] = this.parseFromDateToTimestamp(value[CustomControllerFields.Min]);
        }

        if (value[CustomControllerFields.Max]) {
          filter.range[key][FiltersRangeEnum.Max] = this.parseToDateToTimestamp(value[CustomControllerFields.Max]);
        }

        filters.push(filter);
      }
      else if (((value[CustomControllerFields.Min] || value[CustomControllerFields.Max]) && (value[CustomControllerFields.Type] === FieldType.Integer || value[CustomControllerFields.Type] === FieldType.Float))) {
        const filter = { range: {} };
        const key = value[CustomControllerFields.Name];
        filter.range[key] = {};

        if (value[CustomControllerFields.Min]) {
          filter.range[key][FiltersRangeEnum.Min] = value[CustomControllerFields.Type] === FieldType.Integer ? Math.floor(value[CustomControllerFields.Min]) : Number(value[CustomControllerFields.Min]);
        }

        if (value[CustomControllerFields.Max]) {
          filter.range[key][FiltersRangeEnum.Max] = value[CustomControllerFields.Type] === FieldType.Integer ? Math.floor(value[CustomControllerFields.Max]) : Number(value[CustomControllerFields.Max]);
        }

        filters.push(filter);
      }
    });

    return filters;
  }

  resetFilters(): void {
    const fileTypes = this.form.get(AppSideBarSearchEnum.FileType).value.map(value => ({ ...value, selected: false }));
    const modality = this.form.get(AppSideBarSearchEnum.Modality).value.map(value => ({ ...value, selected: false }));
    const classification = this.form.get(AppSideBarSearchEnum.Classification).value.map(value => ({ ...value, selected: false }));
    const sex = this.form.get(AppSideBarSearchEnum.Sex).value.map(value => ({ ...value, selected: false }));
    const customFieldsList = this.form.get(AppSideBarSearchEnum.CustomFieldsList) as FormArray;
    customFieldsList.clear({ emitEvent: false });

    this.form.setValue({
      [AppSideBarSearchEnum.FileType]: fileTypes,
      [AppSideBarSearchEnum.Modality]: modality,
      [AppSideBarSearchEnum.Classification]: classification,
      [AppSideBarSearchEnum.Project]: '',
      [AppSideBarSearchEnum.From]: '',
      [AppSideBarSearchEnum.SearchTerm]: '',
      [AppSideBarSearchEnum.Sex]: sex,
      [AppSideBarSearchEnum.SubjectAgeFrom]: '',
      [AppSideBarSearchEnum.SubjectAgeTo]: '',
      [AppSideBarSearchEnum.To]: '',
      [AppSideBarSearchEnum.CustomField]: '',
      [AppSideBarSearchEnum.CustomFieldsList]: customFieldsList.value,
    });
  }

  formatNumber(num: number): string {
    if (num < 1000) {
      return num.toString();
    }

    const formatted = (num / 1000).toFixed(1);
    return formatted.endsWith('.0') ? formatted.slice(0, -2) + 'k' : formatted + 'k';
  }

  formatIntegerValue(controller: AbstractControl, key: CustomControllerFields): void {
    const value = controller.value;
    controller.setValue({ ...controller.value, [key]: Math.floor(value[key]) }, { emitEvent: false });
  }

  parseFromDateToTimestamp(dateString: string): number {
    const date = new Date(`${dateString}T00:00:00`);
    return date.getTime();
  }

  parseToDateToTimestamp(dateString: string): number {
    const date = new Date(`${dateString}T23:59:59.999`);
    return date.getTime();
  }

  createFormGroups(buckets: Bucket[]): FormGroup[] {
    return buckets.map(bucket => this.formBuilder.group({
      key: bucket.key,
      doc_count: this.formatNumber(bucket.doc_count),
      selected: false,
    }))
  }

  addCustomField(field: SearchField): void {
    const controls = {
      name: field.name,
      type: field.type,
    };

    if (this.customFiltersWithRange.includes(field.type)) {
      controls[CustomControllerFields.Min] = '';
      controls[CustomControllerFields.Max] = '';
    } else {
      controls[CustomControllerFields.Value] = '';
    }

    const group = this.formBuilder.group(controls);

    if (field.type === FieldType.String) {
      this.subscriptions.customTextInputs.push(group.controls[CustomControllerFields.Value].valueChanges.pipe(
        switchMap(value => this.coreApi.dataexplorer.fieldsAggregate({ field_name: controls.name, search_string: value.toString() })),
      ).subscribe((data: SearchFieldAggregation) => {
        this.typeahead[field.name] = data.buckets.map(bucket => bucket.key);
        this.changeDetector.detectChanges();
      }));
    }

    this.customFields.push(group);
  }

  selectProjectFilter(option: Option): void {
    this.form.get(AppSideBarSearchEnum.Project).setValue(option.value);
    this.selectedProjectFilter = option;
    this.triggers.first.close();
  }

  resetProjectFilter(): void {
    this.form.get(AppSideBarSearchEnum.Project).setValue('');
    this.selectedProjectFilter = null;
  }

  protected selectCustomFieldOptions(name: string, fields: SearchField[]): void {
    const isFieldExisted = this.customFields.controls.some(control => control.value[CustomControllerFields.Name] === name);

    if (!isFieldExisted) {
      this.form.get(AppSideBarSearchEnum.CustomField).setValue('', { emitEvent: false });
      const selectedCustomField = fields.find(field => field.name === name);
      this.addCustomField(selectedCustomField);
    }

    this.triggers.first.close();
  }

  protected onBaseSearchClick(): void {
    this.sidebarService.search.searchMode = SearchMode.Basic;
    this.sidebarService.search.structuredQuery = '';
    this.sidebarService.performSearch();
  }

  protected selectCustomFieldItemOptions(name: string, controller: AbstractControl,): void {
    controller.setValue({ ...controller.value, value: name });
    this.triggers.forEach(trigger => trigger.close());
  }

  private setBuckets(formGroups: FormGroup[], name: AppSideBarSearchEnum): void {
    const bucketFormArray = this.formBuilder.array(formGroups);
    this.form.setControl(name, bucketFormArray);
  }

  private setInitialAgeRange(minAgeTimestamp: string, maxAgeTimestamp: string): void {
    if (minAgeTimestamp) {
      this.form.get(AppSideBarSearchEnum.From).setValue(this.parseDate(minAgeTimestamp))
    }

    if (maxAgeTimestamp) {
      this.form.get(AppSideBarSearchEnum.To).setValue(this.parseDate(maxAgeTimestamp))
    }
  }

  private parseDate(dateString: string): string {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private checkUrl(): void {
    this.isAdvancedSearchMode = this.sidebarService.search.searchMode === SearchMode.Advanced;
    this.changeDetector.detectChanges();
  }
}
